// src/hooks/useAuth.js
import { createContext, useContext, useState, useCallback } from 'react';

const AuthContext = createContext(null);

const DEFAULT_PERMISSIONS = {
  admin: { canViewSchedules: true, canEditSchedules: true, canDeleteSchedules: true, canViewEmployees: true, canEditEmployees: true, canDownloadWorkPlans: true, canViewCitas: true, canEditCitas: true, canManageRoles: true, canApproveChanges: true, canViewPuestos: true, canEditPuestos: true, canViewVacantes: true , canViewAsistencia: true, canManageDevices: true },
  director: { canViewSchedules: true, canEditSchedules: true, canDeleteSchedules: true, canViewEmployees: true, canEditEmployees: true, canDownloadWorkPlans: true, canViewCitas: true, canEditCitas: true, canManageRoles: true, canApproveChanges: true, canViewPuestos: true, canEditPuestos: true, canViewVacantes: true , canViewAsistencia: true, canManageDevices: true },
  supervisor: { canViewSchedules: true, canEditSchedules: true, canDeleteSchedules: true, canViewEmployees: true, canEditEmployees: false, canDownloadWorkPlans: true, canViewCitas: true, canEditCitas: true, canManageRoles: false, canApproveChanges: false, canViewPuestos: true, canEditPuestos: false, canViewVacantes: true , canViewAsistencia: true, canManageDevices: false },
  manager: { canViewSchedules: true, canEditSchedules: false, canDeleteSchedules: false, canViewEmployees: true, canEditEmployees: false, canDownloadWorkPlans: true, canViewCitas: true, canEditCitas: false, canManageRoles: false, canApproveChanges: false, canViewPuestos: false, canEditPuestos: false, canViewVacantes: false , canViewAsistencia: true, canManageDevices: false },
};

const USERS = [
  { id: 1, username: 'admin', password: process.env.REACT_APP_ADMIN_PASS || 'GGPC2024!', name: 'Administrador', role: 'admin' },
  { id: 2, username: 'supervisor1', password: process.env.REACT_APP_SUP1_PASS || 'Super2024!', name: 'Supervisor 1', role: 'supervisor' },
  { id: 3, username: 'supervisor2', password: process.env.REACT_APP_SUP2_PASS || 'Super2024!', name: 'Supervisor 2', role: 'supervisor' },
  { id: 4, username: 'manager', password: process.env.REACT_APP_MGR_PASS || 'Manager2024!', name: 'Gerente RRHH', role: 'manager' },
  { id: 5, username: 'director', password: process.env.REACT_APP_DIR_PASS || 'Director2024!', name: 'Director', role: 'director' },
  ];
const LOGIN_MAX_ATTEMPTS = 5;
const LOGIN_WINDOW_MS = 15 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const session = JSON.parse(sessionStorage.getItem('ggpc_session'));
      if (!session) return null;
      const loginTime = sessionStorage.getItem('ggpc_session_time');
      if (loginTime && Date.now() - parseInt(loginTime) > 8 * 60 * 60 * 1000) { sessionStorage.removeItem('ggpc_session'); sessionStorage.removeItem('ggpc_session_time'); return null; }
      return session;
    } catch { return null; }
  });
  const [permVersion, setPermVersion] = useState(0);

const getPermissions = useCallback((role) => {
  try { const saved = localStorage.getItem('ggpc_permissions_' + role); if (saved) return { ...(DEFAULT_PERMISSIONS[role] || {}), ...JSON.parse(saved) }; } catch {}
  return { ...(DEFAULT_PERMISSIONS[role] || {}) };
}, [permVersion]);

const updatePermissions = useCallback((role, permissions) => {
  localStorage.setItem('ggpc_permissions_' + role, JSON.stringify(permissions));
  setPermVersion(v => v + 1);
}, []);

const login = (username, password) => {
  const now = Date.now();
  let att = [];
  try { att = JSON.parse(sessionStorage.getItem('ggpc_attempts') || '[]'); } catch {}
  att = att.filter(t => now - t < LOGIN_WINDOW_MS);
  if (att.length >= LOGIN_MAX_ATTEMPTS) { const w = Math.ceil((LOGIN_WINDOW_MS - (now - att[0])) / 60000); throw new Error('Demasiados intentos. Espera ' + w + ' min.'); }
  const found = USERS.find(u => u.username === username.trim() && u.password === password);
  if (found) {
    sessionStorage.removeItem('ggpc_attempts');
    const safe = { id: found.id, username: found.username, name: found.name, role: found.role };
    sessionStorage.setItem('ggpc_session', JSON.stringify(safe));
    sessionStorage.setItem('ggpc_session_time', now.toString());
    setUser(safe);
    return true;
  }
  att.push(now);
  sessionStorage.setItem('ggpc_attempts', JSON.stringify(att));
  return false;
};

const logout = () => { sessionStorage.removeItem('ggpc_session'); setUser(null); };

const can = useCallback((permission) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return !!getPermissions(user.role)[permission];
}, [user, getPermissions]);

// Guarda solicitud en Supabase via dynamic import (evita circular deps)
const addApprovalRequest = async (request) => {
  const payload = {
    type: request.type || 'edit_schedule',
    action: request.action || request.type || 'Cambio de horario',
    requested_by: request.requestedBy || (user ? user.name : 'Desconocido'),
    employee_name: request.employeeName || null,
    date: request.date || null,
    shift: request.shift || null,
    notes: request.notes || null,
    status: 'pending',
  };

  try {
    // FIXED: correct import path from hooks/ to supabaseClient
  const { supabase } = await import('../supabaseClient');

  const { data, error } = await supabase
    .from('approval_requests')
    .insert([payload])
    .select();

  if (error) {
    console.error('Error guardando solicitud:', error);
    alert('Error guardando solicitud: ' + error.message);
    return null;
  }

  window.dispatchEvent(new CustomEvent('ggpc_approval_added'));

  return data?.[0] || payload;
  } catch (e) {
    console.error('Approval request error:', e);
    alert('Error conectando con Supabase: ' + e.message);
    return null;
  }
};

return (
  <AuthContext.Provider value={{ user, login, logout, can, DEFAULT_PERMISSIONS, getPermissions, updatePermissions, addApprovalRequest }}>
{children}
</AuthContext.Provider>
);
}

export function useAuth() {
  return useContext(AuthContext);
}
